import * as React from "react";
import { Form, redirect, useActionData, useLoaderData, useNavigate } from "react-router-dom";
import "./project-new.css";
import { Button, Grid, GridColumn, Header, Message } from "semantic-ui-react";
import { Constants } from "../../../globals/constants";
import { RootStore } from "../../../stores/root-store";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import { useState } from "react";
import ConstructionPermitNew from "./construction-permit-new";
import LandUsePlanChangeNew from "./land-use-plan-change-new";
import LandfillSiteNew from "./landfill-site-new";
import DezoningNew from "./dezoning-new";
import SoilImprovementNew from "./soil-improvement-new";
import { TransportError } from "../../../stores/data-transporter";
import { KeyValueStore } from "../../../stores/key-value/key-value-store";
import { AuthenticationStore } from "../../../stores/authentication/authentication-store";
import { Contact } from "../../../stores/contact/contact-store";
import {
    CreationProjectDto,
    ProjectDtoFactory,
    ProjectDtoValidator,
    UsageProjectDto
} from "../../../stores/project/project-dto";
import { CreationProject, Project } from "../../../stores/project/project";
import { Municipality } from "../../../stores/municipality/municipality-store";
import { getLabel } from "../../../reflection/reflection";

type LoadResult = {
    contacts: Contact[],
    municipalities: Municipality[],
    keyValueStore: KeyValueStore,
    authenticationStore: AuthenticationStore
}

export async function loader(rootStore: RootStore) {
    const rootStoreSession = rootStore.newSession();
    const contacts = await rootStoreSession.contactStore.loadAll();
    const municipalities = await rootStoreSession.municipalityStore.loadAll();
    const keyValueStore = rootStore.keyValueStore;
    const authenticationStore = rootStore.authenticationStore;
    return { contacts, municipalities, keyValueStore, authenticationStore };
}

export async function action(request: Request, rootStore: RootStore) {
    const formData = await request.formData();
    const rootStoreSession = rootStore.session;
    const projectDto = ProjectDtoFactory.makeProjectDto(formData, rootStoreSession.municipalityStore, rootStore.keyValueStore);
    const valid = ProjectDtoValidator.checkRequiredFields(projectDto);

    if (valid) {
        try {
            let project: Project;
            const rootStoreSession = rootStore.session;

            if (projectDto.category === Constants.projectCategoryCreation) {
                project = await rootStoreSession.projectStore.addCreationProject(projectDto as CreationProjectDto);
            } else {
                project = await rootStoreSession.projectStore.addUsageProject(projectDto as UsageProjectDto);

                if (projectDto.type === Constants.projectTypeLandfillSite) {
                    formData.set("projectType", Constants.projectTypeRecultivation);
                    const creationProjectDto = ProjectDtoFactory.makeProjectDto(formData, rootStoreSession.municipalityStore, rootStore.keyValueStore);
                    const creationProject = await rootStoreSession.projectStore.addCreationProject(creationProjectDto as CreationProjectDto);
                    await rootStoreSession.compensationStore.add({
                        id: 0,
                        fffUsageProjectId: project.id,
                        fffCreationProjectId: creationProject.id,
                        areaNek2: null,
                        areaNek3: null,
                        areaNek4: null,
                        areaNek5: null,
                        areaNek6: null,
                        areaNekUnknown: null,
                        area: null,
                        remarks: null
                    });
                }
            }

            return redirect(`/projekte/${project.categoryKey}/${project.id}`);
        } catch (e: TransportError) {
            switch (e.statusCode) {
                case 400:
                    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
                default:
                    return "Es ist ein unerwarteter Fehler aufgetreten!";
            }
        }
    }

    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
}

export default function ProjectNew(): JSX.Element {
    const {
        contacts,
        municipalities,
        keyValueStore,
        authenticationStore
    } = useLoaderData() as LoadResult;
    const [selectedProjectType, setSelectedProjectType] = useState<string>("baubewilligung");
    const errorMsg = useActionData() as string;
    const navigate = useNavigate();

    return (
        <div className="projectNew">
            <Header>Neues Projekt anlegen</Header>
            {errorMsg && (
                <Message error={true} compact={true}>
                    {errorMsg}
                </Message>
            )}
            <Form
                method="post"
                className="projectNewForm"
                autoComplete="off">
                <LabeledInput
                    label={getLabel(CreationProject.prototype, "fullType")}
                    inputElement={
                        <select name="projectType" className="ui selection dropdown"
                                onChange={(e) => setSelectedProjectType(e.target.value)}>
                            {keyValueStore.projectTypes.map((t) => (
                                <option
                                    key={t.key}
                                    value={t.key}
                                    selected={selectedProjectType === t.key}
                                >
                                    {t.value}
                                </option>
                            ))}
                        </select>
                    }
                    required={true}
                />
                {selectedProjectType === Constants.projectTypeConstructionPermit &&
                    <ConstructionPermitNew municipalities={municipalities} keyValueStore={keyValueStore}/>}
                {selectedProjectType === Constants.projectTypeLandUsePlanChange &&
                    <LandUsePlanChangeNew municipalities={municipalities} keyValueStore={keyValueStore}/>}
                {selectedProjectType === Constants.projectTypeLandfillSite &&
                    <LandfillSiteNew municipalities={municipalities} contacts={contacts}
                                     keyValueStore={keyValueStore}/>}
                {selectedProjectType === Constants.projectTypeSoilImprovement &&
                    <SoilImprovementNew municipalities={municipalities} contacts={contacts}
                                        keyValueStore={keyValueStore}/>}
                {selectedProjectType === Constants.projectTypeDezoning &&
                    <DezoningNew municipalities={municipalities} contacts={contacts} keyValueStore={keyValueStore}/>}
                <input type="hidden" name="ownerId" value={authenticationStore.user?.id}/>
                <Grid columns={2} relaxed={true}>
                    <GridColumn>
                        <Button
                            type="button"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            Abbrechen
                        </Button>
                        <Button type="submit">Projekt anlegen</Button>
                    </GridColumn>
                    <GridColumn>
                    </GridColumn>
                </Grid>
            </Form>
        </div>
    );
}
